// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '../model3d/container.service';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleUtils } from '@article/util/article-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ReplaceLabelingUtils } from '@project/components/label/replace-labeling-helper/replace-labeling-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { Injectable } from '@angular/core';
import { InsertArticleUtils } from '@project/components/article/util/insert-article-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ArticleConnectionService } from '../model3d/article-connection.service';
import { ReplaceDynamicArticleService } from '@article/helper/replace/replace-dynamic-article.service';

@Injectable({ providedIn: 'root' })
export class LabelStripArticleManager {
  constructor(
    private _insertArticleUtils: InsertArticleUtils,
    private _articleContainerUtils: ArticleContainerUtils,
    private _insertArticleHelperFactory: InsertArticleHelperFactory,
    private _componentUtils: ContainerService,
    private _replaceLabelingUtils: ReplaceLabelingUtils,
    private _articleConnectionUtils: ArticleConnectionService,
    private replaceDynamicArticleService: ReplaceDynamicArticleService,
    private containerService: ContainerService,
  ) {}

  private getCommonLabelStripsOnAdaptersByNeighborArticles(
    targetArticleContainer: ArticleContainer,
    excludeLabelStripsConnectedToTargetContainer: boolean
  ): string[] {
    if (!this._articleConnectionUtils.isArticleConnectedToMountingRail(targetArticleContainer)) {
      return [];
    }
    const insertHelper: InsertArticleHelper = this._insertArticleHelperFactory.createInstanceByConnectedArticleUsage(
      targetArticleContainer.articleUsage
    );
    if (!insertHelper) {
      return [];
    }

    const leftContainer: ArticleContainer = insertHelper.getLeftArticleContainer(
      targetArticleContainer.articleGUID,
      LabelingUtils.TYPE_IDS_IGNORED_BY_STRIP
    );
    const rightContainer: ArticleContainer = insertHelper.getRightArticleContainer(
      targetArticleContainer.articleGUID,
      LabelingUtils.TYPE_IDS_IGNORED_BY_STRIP
    );
    if (!leftContainer || !rightContainer) {
      return [];
    }

    let commonLabelStripIds: string[] = this.getCommonLabelStripsOnAdaptersByLeftRightArticles(leftContainer, rightContainer);
    if (excludeLabelStripsConnectedToTargetContainer) {
      const labelStripIdsByTargetArticle: string[] = this.getAllConnectedLabelStripsOnAdapters(targetArticleContainer);
      commonLabelStripIds = commonLabelStripIds.filter(ArrayUtils.notInArray, labelStripIdsByTargetArticle);
    }
    return commonLabelStripIds;
  }

  private getCommonLabelStripsOnAdaptersByLeftRightArticles(leftContainer: ArticleContainer, rightContainer: ArticleContainer): string[] {
    return this._replaceLabelingUtils
      .getCommonLabelStripUsageIdsByLeftRightArticles(leftContainer, rightContainer)
      .filter((container) => this.isStripConnectedToAdapters(container))
      .map((container) => container.articleGUID);
  }

  private getAllConnectedLabelStripsOnAdapters(parentContainer: ArticleContainer): string[] {
    return this._replaceLabelingUtils
      .getConnectedLabelStripsToContainer(parentContainer)
      .filter((container) => this.isStripConnectedToAdapters(container))
      .map((container) => container.articleGUID);
  }

  private isStripConnectedToAdapters(container: ArticleContainer): boolean {
    if (!container || !ArticleUtils.isDynamicLabelingArticle(container.article)) {
      return false;
    }
    const parentUsageId: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(container.articleUsage);
    if (!parentUsageId) {
      return false;
    }
    const parentContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(parentUsageId);
    return !this._articleConnectionUtils.isArticleConnectedToMountingRail(parentContainer);
  }

  public getLabelStripsOnAdaptersToDetachByMovedArticles(
    moveArticlesMap: Dictionary<string, Vector3>,
    articlesOrderChanged: boolean,
    revertLabelStripPosition: boolean
  ): ArticleContainer[] {
    if (!moveArticlesMap) {
      return [];
    }

    const labelStripContainersToDetach: ArticleContainer[] = [];
    const processedArticleGuids: string[] = [];
    for (const movedUsageId of moveArticlesMap.keys()) {
      const movedArticleContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(movedUsageId);
      if (!movedArticleContainer || LabelingUtils.isArticleIgnoredByLabelStrip(movedArticleContainer.article)) {
        continue;
      }
      if (articlesOrderChanged) {
        const labelStripIdsByNeighbors: string[] = this.getCommonLabelStripsOnAdaptersByNeighborArticles(movedArticleContainer, true);
        const labelStripContainersByNeighbors: ArticleContainer[] = labelStripIdsByNeighbors.map((id) => this.containerService.findArticleContainerInGlobal(id));
        ArrayUtils.mergeArrays(labelStripContainersToDetach, labelStripContainersByNeighbors);
        ArrayUtils.mergeArrays(processedArticleGuids, labelStripIdsByNeighbors);
      }
      const connectedContainers: ArticleContainer[] = this._articleContainerUtils.getConnectedArticlesRecursively(movedArticleContainer, true);
      for (const connectedContainer of connectedContainers) {
        if (processedArticleGuids.indexOf(connectedContainer.articleGUID) >= 0) {
          continue;
        }
        if (this.isLabelStripDeletionRequired(connectedContainer, moveArticlesMap, revertLabelStripPosition)) {
          labelStripContainersToDetach.push(connectedContainer);
        }
        processedArticleGuids.push(connectedContainer.articleGUID);
      }
    }
    return labelStripContainersToDetach;
  }

  private isLabelStripDeletionRequired(
    articleContainer: ArticleContainer,
    moveArticlesMap: Dictionary<string, Vector3>,
    revertLabelStripPosition: boolean
  ): boolean {
    if (articleContainer == null || !ArticleUtils.isDynamicLabelingArticle(articleContainer.article)) {
      return false;
    }
    const backwardUsageIds: string[] = ReferenceArticleUsageUtils.getBackwardReferenceArticleUsageIds(articleContainer.articleUsage);
    if (backwardUsageIds.length === 0) {
      return false;
    }
    const backwardConnectedUsageIds: string[] = this.replaceDynamicArticleService.getBackwardUsageIdsByCheckFunction(
      articleContainer,
      (container: ArticleContainer) => this._articleConnectionUtils.isArticleConnectedToMountingRail(container)
    );
    ArrayUtils.mergeArrays(backwardUsageIds, backwardConnectedUsageIds);
    const isDeletionRequired: boolean = !this._replaceLabelingUtils.isMoveMapContainsAllUsageIdsWithSameOffset(moveArticlesMap, backwardUsageIds);
    if (isDeletionRequired && revertLabelStripPosition) {
      const targetBackwardUsageId: string = backwardConnectedUsageIds.length > 0 ? backwardConnectedUsageIds[0] : null;
      if (targetBackwardUsageId && moveArticlesMap.getValue(targetBackwardUsageId)) {
        this._insertArticleUtils.moveSingleArticleByOffset(articleContainer, moveArticlesMap.getValue(targetBackwardUsageId), true);
      }
    }
    return isDeletionRequired;
  }

  public processDetachedLabelStrip(articleContainer: ArticleContainer): void {
    if (!ArticleUtils.isDynamicLabelingArticle(articleContainer.article)) {
      return;
    }
    const backwardReferenceUsageIds: string[] = ReferenceArticleUsageUtils.getBackwardReferenceArticleUsageIds(articleContainer.articleUsage);
    for (const backwardUsageId of backwardReferenceUsageIds) {
      const backwardContainer: ArticleContainer = this._componentUtils.findArticleContainerInGlobal(backwardUsageId);
      // var backwardArticleUsage:ArticleUsageVO = mainModel.getArticleUsage(backwardUsageId);
      if (backwardContainer && backwardContainer.articleUsage) {
        ReferenceArticleUsageUtils.removeForwardReferenceArticleUsageId(backwardContainer.articleUsage, articleContainer.articleGUID);
      }
    }
  }
}
